/**
 * TASK-923 — the ONE place a surface asks "may this user take this action on this signal?"
 *
 * The server has shipped `allowed_roles` in every action-policy contract since TASK-732, and
 * not one surface read it. Four of them each wrote their own check on
 * `allowed_dispositions` alone — `ChangeRadar.policyAllows`, `SignalActions`,
 * `AlertGroupCard`, `AlertCard` — so `escalate` and `resolve` rendered to every user on a
 * compliance row and the server refused the write. The advisor got a button that 403s, which
 * is worse than no button: it reads as a broken product rather than as a boundary.
 *
 * One helper rather than a fifth guard, for the reason the whole policy catalogue exists: the
 * surface renders what the server decided, and four copies of a rule are four chances to
 * drift from it.
 *
 * Roles are COARSE system names the contract deliberately exposes (`to_contract`: "they let
 * the UI say 'requires a compliance officer' instead of offering a button that will 403").
 * This is presentation, never enforcement — `AlertService._update_status` is the seam that
 * actually decides, and it does not trust anything here.
 */
import type { ActionPolicy } from '../types/dashboard';

/**
 * The dispositions a role may actually be refused, and therefore the only ones worth hiding.
 *
 * `validate_disposition` role-checks everything but `read` — but it is only ever CALLED at
 * `AlertService._update_status`, and the module says which actions cross that seam:
 * "`investigate`, `escalate` and `assign` never mutate alert state, so there is nothing to
 * enforce at that seam; they are projection-only."
 *
 * So `investigate` is never refused, and hiding it would remove navigation the server would
 * happily allow. The first cut of this change gated every disposition and did exactly that: a
 * `reprofile_due` group rendered ZERO buttons to an advisor — precisely the defect TASK-820
 * exists to have fixed, reintroduced for a new reason. Two of that ticket's tests caught it.
 *
 * `assign` IS gated, and the reasoning above is why it nearly was not. There is a SECOND
 * caller of `validate_disposition` that the enforcement-seam paragraph does not mention:
 * `data-ingestion/routes/task_routes.py` (TASK-761) validates `disposition_intent or
 * "assign"` against the `X-User-Role` header whenever the new task binds to a persisted
 * alert. So an advisor clicking "Make task" on `kyc_expired`, `reprofile_due` or
 * `review_action_required` gets a 403 from the task endpoint — the exact defect this helper
 * exists to prevent, on a path the alert seam never sees. Review caught it.
 *
 * `escalate` is gated despite being projection-only at the alert seam: it too travels as a
 * `disposition_intent`, and TASK-923's criterion names it.
 *
 * ⚠️ This is a frontend mirror of TWO server rules that the contract ships NEITHER of —
 * `MUTATING_DISPOSITIONS` and the task-creation gate. That is a real drift risk and it has
 * already bitten once, here. The durable fix is for `to_contract()` to say which dispositions
 * are role-enforced; until then this list is pinned by test and by the comment above.
 */
export const ROLE_GATED_DISPOSITIONS: readonly string[] = [
  'dismiss', 'snooze', 'resolve', 'escalate', 'assign',
];

/**
 * Is `action` offerable to a user holding `role`?
 *
 * No policy → permitted. That is the pre-existing contract ("no policy → no restriction") and
 * it must not tighten here: an older payload or an unlisted pair would otherwise render a row
 * with no actions at all, which looks identical to a broken page.
 *
 * `role` undefined → treated as "role unknown", and a role-restricted action is NOT offered.
 * Failing closed is right for the one thing this function exists to gate: the alternative
 * shows compliance controls to whoever loaded the page before their identity resolved.
 */
export function policyPermits(
  policy: ActionPolicy | null | undefined,
  action: string,
  role?: string | null,
): boolean {
  if (!policy) return true;
  if (!policy.allowed_dispositions.includes(action)) return false;
  // `null` roles = any authenticated caller. The server sets this deliberately on entries
  // that must not narrow if the role list changes.
  if (!policy.allowed_roles) return true;
  // Navigation and task creation are never refused — see ROLE_GATED_DISPOSITIONS.
  if (!ROLE_GATED_DISPOSITIONS.includes(action)) return true;
  return Boolean(role) && policy.allowed_roles.includes(role as string);
}

/**
 * Why a policy-allowed action is not being offered to THIS user, in words a person can act
 * on — or null when it is offered.
 *
 * Only ever about ROLE. A disposition the policy does not allow at all is explained by the
 * server's own `absent_disposition_notes` (TASK-820), which is curated copy; inventing a
 * second explanation here would put an engineer's words next to the owner's.
 */
export function roleDenialReason(
  policy: ActionPolicy | null | undefined,
  action: string,
  role?: string | null,
): string | null {
  if (!policy || !policy.allowed_roles) return null;
  if (!policy.allowed_dispositions.includes(action)) return null;
  if (policyPermits(policy, action, role)) return null;
  const roles = policy.allowed_roles.map((r) => r.replace(/_/g, ' ')).join(' or ');
  return `Requires a ${roles}`;
}
